import { AmortizationType, PricingFieldName, amortizationTypeDisplay } from '@api';
import { FormControl, FormLabel, Typography } from '@mui/material';
import {
    CurrencyField,
    PercentField,
    Radio,
    RadioGroup,
    Switch,
    TextField,
    isAWholeNumber,
    isAtLeast,
    isAtMost
} from '@tsp-ui/core/components';
import { useRenderTogglableEnumOptions } from '@utils';
import { ClientContext } from '@views/AuthenticatedRouteSwitch';
import clsx from 'clsx';
import { useContext } from 'react';
import { useWatch } from 'react-hook-form';

import { ManualLoanEntryFormValues } from '../ManualLoanEntryPage';

import styles from './LoanStep.module.scss';
import ManualLoanEntryStep, { ManualLoanEntryStepProps } from './ManualLoanEntryStep';


interface LoanStepProps extends Pick<ManualLoanEntryStepProps, 'onPreviousButtonClick'> {
    isFieldEnabled: (fieldName: PricingFieldName) => boolean;
}

export default function LoanStep({ isFieldEnabled, onPreviousButtonClick }: LoanStepProps) {
    const amorType = useWatch<ManualLoanEntryFormValues>({ name: 'amorType' });

    const renderTogglableEnumOptions = useRenderTogglableEnumOptions();
    const { configurableValues } = useContext(ClientContext);
    const allowFixed = configurableValues?.togglableValues.amortizationType.includes(AmortizationType.FIXED);
    const allowAdjustable = configurableValues?.togglableValues.amortizationType.includes(AmortizationType.ADJUSTABLE);

    return (
        <ManualLoanEntryStep onPreviousButtonClick={onPreviousButtonClick}>
            <div>
                <FormLabel>
                    Loan details
                </FormLabel>

                <div className={styles.loanDetailsContainer}>
                    <TextField<ManualLoanEntryFormValues>
                        name="customerLoanNumber"
                        label="Loan number"
                        required
                        maxLength={100}
                    />

                    <TextField<ManualLoanEntryFormValues>
                        name="universalLoanIdentifier"
                        label="Universal loan ID (optional)"
                        maxLength={50}
                    />

                    <CurrencyField<ManualLoanEntryFormValues>
                        name="loanAmount"
                        label="Total loan amount"
                        required={isFieldEnabled(PricingFieldName.LOAN_AMOUNT)}
                        rules={isAtLeast(1)}
                    />

                    <CurrencyField<ManualLoanEntryFormValues>
                        name="baseLoanAmount"
                        label="Base loan amount"
                        rules={isAtLeast(1)}
                        required
                    />

                    <TextField<ManualLoanEntryFormValues>
                        name="loanType"
                        label="Loan type"
                        className={styles.sectionStart}
                        required={isFieldEnabled(PricingFieldName.LOAN_TYPE)}
                        select
                        maxLength={100}
                    >
                        {renderTogglableEnumOptions('loanType')}
                    </TextField>

                    <TextField<ManualLoanEntryFormValues>
                        name="loanLimitType"
                        label="Loan limit type"
                        className={styles.sectionStart}
                        required={isFieldEnabled(PricingFieldName.LOAN_LIMIT_TYPE)}
                        select
                        maxLength={100}
                    >
                        {renderTogglableEnumOptions('loanLimitType')}
                    </TextField>

                    <TextField<ManualLoanEntryFormValues>
                        name="loanTerm"
                        label="Term (months)"
                        type="number"
                        required={isFieldEnabled(PricingFieldName.LOAN_TERM)}
                        rules={{
                            ...isAtLeast(1),
                            ...isAWholeNumber
                        }}
                    />

                    <PercentField<ManualLoanEntryFormValues>
                        name="interestRate"
                        label="Interest rate"
                        required={isFieldEnabled(PricingFieldName.INTEREST_RATE)}
                        rules={isAtLeast(0)}
                        noConversion
                    />

                    <TextField<ManualLoanEntryFormValues>
                        name="lockPeriod"
                        label="Lock period (days)"
                        required={isFieldEnabled(PricingFieldName.LOCK_PERIOD)}
                        select
                    >
                        {renderTogglableEnumOptions('lockPeriod')}
                    </TextField>

                    <TextField<ManualLoanEntryFormValues>
                        name="loanFICO"
                        label="Loan FICO"
                        type="number"
                        rules={{
                            ...isAtLeast(300),
                            ...isAtMost(850)
                        }}
                    />

                    <TextField<ManualLoanEntryFormValues>
                        name="dti"
                        label="Debt to income ratio"
                        type="number"
                        required={isFieldEnabled(PricingFieldName.DTI)}
                        rules={isAtLeast(0)}
                    />

                    <br />

                    <FormControl
                        component="fieldset"
                        className={styles.sectionStart}
                    >
                        <FormLabel
                            component="legend"
                            required={isFieldEnabled(PricingFieldName.AMORT_TYPE)}
                        >
                            Amortization type
                        </FormLabel>

                        <RadioGroup<ManualLoanEntryFormValues>
                            name="amorType"
                            defaultValue={AmortizationType.FIXED}
                        >
                            {allowFixed && (
                                <Radio
                                    value={AmortizationType.FIXED}
                                    label={amortizationTypeDisplay[AmortizationType.FIXED]}
                                />
                            )}

                            {allowAdjustable && (
                                <Radio
                                    value={AmortizationType.ADJUSTABLE}
                                    label={amortizationTypeDisplay[AmortizationType.ADJUSTABLE]}
                                />
                            )}

                            {!allowFixed && !allowAdjustable && (
                                <Typography>
                                    No amortization types are enabled for this client.
                                </Typography>
                            )}
                        </RadioGroup>
                    </FormControl>

                    {amorType === AmortizationType.ADJUSTABLE ? (
                        <FormControl
                            className={styles.sectionStart}
                            component="fieldset"
                        >
                            <FormLabel
                                component="legend"
                                required
                            >
                                Amortization details
                            </FormLabel>

                            <div className={styles.amortizationDetailsFields}>
                                <CurrencyField<ManualLoanEntryFormValues>
                                    name="armMargin"
                                    label="Margin"
                                    size="small"
                                    required
                                    hideRequiredIndicator
                                    rules={isAtLeast(0)}
                                />

                                <CurrencyField<ManualLoanEntryFormValues>
                                    name="armInitialCap"
                                    label="Initial cap"
                                    size="small"
                                    required
                                    hideRequiredIndicator
                                    rules={isAtLeast(0)}
                                />

                                <CurrencyField<ManualLoanEntryFormValues>
                                    name="armSubsequentCaps"
                                    label="Subsequent cap"
                                    size="small"
                                    required
                                    hideRequiredIndicator
                                    rules={isAtLeast(0)}
                                />

                                <CurrencyField<ManualLoanEntryFormValues>
                                    name="armLifeCap"
                                    label="Life cap"
                                    size="small"
                                    required
                                    hideRequiredIndicator
                                    rules={isAtLeast(0)}
                                />
                            </div>
                        </FormControl>
                    ) : <div />}

                    <Switch<ManualLoanEntryFormValues>
                        name="escrowsFlag"
                        label="Taxes & insurance escrowed"
                        className={styles.fullWidth}
                        required={isFieldEnabled(PricingFieldName.ESCROWS_FLAG)}
                    />

                    <Switch<ManualLoanEntryFormValues>
                        name="interestOnlyFlag"
                        label="Interest only"
                        className={clsx(styles.fullWidth, styles.interestSwitch)}
                        required={isFieldEnabled(PricingFieldName.INTEREST_ONLY_FLAG)}
                    />
                </div>
            </div>
        </ManualLoanEntryStep>
    );
}
